const { isBundleLoadError } = require('./bundle-load-error');
const ClientErrorReporting = require('services/client-error-reporting');

const internals = {};

module.exports = {
    buildRouteRecoveryReport,
    claimRouteErrorRecovery,
    isRecoverableRouteError,
    reportRouteErrorRecovery,
    shouldSimulateRouteError,
    startRouteRecoverySimulation
};

internals.RECOVERY_ATTEMPT_KEY = 'nearpeer:route-error-recovery';
internals.RECOVERY_ATTEMPT_TTL_MS = 5 * 60 * 1000;
internals.MAX_RECOVERY_ATTEMPTS = 1;
internals.PROPERTY_ACCESS_ERROR_PATTERN = /Cannot read properties of (?:undefined|null) \(reading ['"].+['"]\)/i;
internals.SIMULATION_KEY = 'nearpeer:route-error-recovery-simulation';
internals.SIMULATION_FAILURE_COUNT = internals.MAX_RECOVERY_ATTEMPTS + 1;
internals.documentId = `${Date.now()}:${Math.random()}`;
internals.simulationActive = null;

function getErrorMessage(error) {

    return String(error?.message || error || 'Unknown route error');
}

function isRecoverableRouteError(error) {

    return isBundleLoadError(error) || internals.PROPERTY_ACCESS_ERROR_PATTERN.test(getErrorMessage(error));
}

function buildRouteRecoveryReport({ error, errorInfo } = {}) {

    return ClientErrorReporting.buildClientErrorReport({
        error,
        errorInfo,
        source: 'react.route-boundary',
        type: 'route_error_recovery',
        recovery: internals.getRecoveryState()
    });
}

async function reportRouteErrorRecovery({ error, errorInfo } = {}) {

    return ClientErrorReporting.reportClientError({
        error,
        errorInfo,
        source: 'react.route-boundary',
        type: 'route_error_recovery',
        recovery: internals.getRecoveryState()
    });
}

function claimRouteErrorRecovery({
    error,
    now = Date.now(),
    pathname = typeof window !== 'undefined' ? window.location?.pathname : '',
    storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null
}) {

    if (!isRecoverableRouteError(error) || !storage) {
        return false;
    }

    const normalizedPathname = pathname || '';
    const signature = `${normalizedPathname}:${getErrorMessage(error)}`;

    try {
        const previousAttempt = JSON.parse(storage.getItem(internals.RECOVERY_ATTEMPT_KEY) || 'null');
        const previousWindowStartedAt = previousAttempt?.windowStartedAt ?? previousAttempt?.at;
        const isCurrentAttemptWindow = previousAttempt?.pathname === normalizedPathname
            && now - previousWindowStartedAt < internals.RECOVERY_ATTEMPT_TTL_MS;
        const attemptCount = isCurrentAttemptWindow ? previousAttempt?.attemptCount || 1 : 0;

        if (attemptCount >= internals.MAX_RECOVERY_ATTEMPTS) {
            return false;
        }

        const nextAttempt = JSON.stringify({
            at: now,
            attemptCount: attemptCount + 1,
            pathname: normalizedPathname,
            signature,
            windowStartedAt: isCurrentAttemptWindow ? previousWindowStartedAt : now
        });

        storage.setItem(internals.RECOVERY_ATTEMPT_KEY, nextAttempt);

        // Only reload when the loop-prevention marker was actually persisted.
        return storage.getItem(internals.RECOVERY_ATTEMPT_KEY) === nextAttempt;
    }
    catch (err) {
        return false;
    }
}

function startRouteRecoverySimulation({
    storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null
} = {}) {

    if (!storage) {
        return false;
    }

    const simulation = JSON.stringify({
        lastDocumentId: null,
        remainingFailures: internals.SIMULATION_FAILURE_COUNT
    });

    try {
        storage.removeItem(internals.RECOVERY_ATTEMPT_KEY);
        storage.setItem(internals.SIMULATION_KEY, simulation);

        internals.simulationActive = storage.getItem(internals.SIMULATION_KEY) === simulation;
        return internals.simulationActive;
    }
    catch (err) {
        return false;
    }
}

function shouldSimulateRouteError({
    documentId = internals.documentId,
    storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null
} = {}) {

    if (!storage) {
        return false;
    }

    if (internals.simulationActive === false) {
        return false;
    }

    try {
        const simulation = JSON.parse(storage.getItem(internals.SIMULATION_KEY) || 'null');

        if (!simulation) {
            internals.simulationActive = false;
            return false;
        }

        internals.simulationActive = true;

        // React may retry a failed render. Keep throwing within the same document
        // without consuming another simulated page-load failure.
        if (simulation.lastDocumentId === documentId) {
            return true;
        }

        if (simulation.remainingFailures <= 0) {
            storage.removeItem(internals.SIMULATION_KEY);
            internals.simulationActive = false;
            return false;
        }

        storage.setItem(internals.SIMULATION_KEY, JSON.stringify({
            lastDocumentId: documentId,
            remainingFailures: simulation.remainingFailures - 1
        }));

        return true;
    }
    catch (err) {
        return false;
    }
}

internals.getRecoveryState = (
    storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null
) => {

    if (!storage) {
        return null;
    }

    try {
        const recovery = JSON.parse(storage.getItem(internals.RECOVERY_ATTEMPT_KEY) || 'null');

        if (!recovery) {
            return null;
        }

        return {
            attemptCount: recovery.attemptCount || 1,
            maxAttempts: internals.MAX_RECOVERY_ATTEMPTS,
            triggeredAt: recovery.at || null,
            windowStartedAt: recovery.windowStartedAt || recovery.at || null
        };
    }
    catch (err) {
        return null;
    }
};

if (process.env.NODE_ENV === 'test') {
    module.exports._internals = internals;
}
